import { Getter, Setter, createLens } from '@dhmk/zustand-lens';
import { Draft } from 'immer';
import {
  GraphEvaluation,
  GraphId,
  NodeId,
  NodeEvaluation,
  NodeEvaluationStatus,
  EvaluationValue,
  EvaluationError,
  EvaluationValueKind,
} from '../../components/graph-editor/types/graph';
import type { GraphsSlice } from '.';
import { ok, err, Result } from 'neverthrow';
import { TERMINAL_NODE_ID } from '../../components/graph-editor/config';

export enum EvaluationSliceError {
  NotFound,
}

export interface IEvaluation {
  get: () => GraphEvaluation;
  getGraphId: () => GraphId;
  set: (evaluation: GraphEvaluation) => void;

  setNodeTemplate: (nodeId: NodeId, template: any) => void;
  getNodeTemplate: (id: NodeId) => Readonly<any>;

  setNodeEvaluation: (nodeId: NodeId, evaluation: NodeEvaluation) => void;
  getNodeEvaluation: (id: NodeId) => Readonly<NodeEvaluation> | undefined;

  getNodeStatus: (id: NodeId) => Readonly<NodeEvaluationStatus>;
  getNodeValue: (id: NodeId) => Readonly<EvaluationValue>;
  getNodeError: (id: NodeId) => Readonly<EvaluationError> | null;

  getGraphTemplate: () => any;
  getGraphEvaluation: () => Result<
    Readonly<NodeEvaluation>,
    EvaluationSliceError
  >;
}

export const createEvaluationAPI = (
  _set: Setter<Draft<GraphsSlice>>,
  _get: Getter<Draft<GraphsSlice>>,
  graphId: GraphId
): IEvaluation => {
  const [set, get] = createLens(_set, _get, ['evaluations', graphId]) as [
    Setter<Draft<GraphEvaluation>>,
    Getter<Draft<GraphEvaluation>>
  ];

  const _getNodeEvaluation = (
    id: NodeId
  ): Result<NodeEvaluation, EvaluationSliceError> => {
    if (Object.hasOwn(get().nodes, id)) return ok(get().nodes[id]);
    return err(EvaluationSliceError.NotFound);
  };

  const _getNodeTemplate = (id: NodeId): Result<any, EvaluationSliceError> => {
    if (Object.hasOwn(get().nodeTemplates, id))
      return ok(get().nodeTemplates[id]);
    return err(EvaluationSliceError.NotFound);
  };

  return {
    get: () => get(),
    getGraphId: () => graphId,
    set: (evaluation) => {
      set(() => evaluation);
    },

    setNodeTemplate: (nodeId, template) => {
      set((s) => {
        s.nodeTemplates[nodeId] = template;
      });
    },
    getNodeTemplate: (id) => {
      return get().nodeTemplates[id];
    },
    setNodeEvaluation: (nodeId, evaluation) => {
      set((s) => {
        s.nodes[nodeId] = evaluation;
      });
    },
    getNodeEvaluation: (id) => {
      return get().nodes[id];
    },
    getNodeStatus: (id) => {
      return _getNodeEvaluation(id).match(
        (v) => v.status,
        (e) => NodeEvaluationStatus.Idle
      );
    },
    getNodeValue: (id) => {
      return _getNodeEvaluation(id).match(
        (v) => v.valueRef,
        (e) => ({ kind: EvaluationValueKind.Empty })
      );
    },
    getNodeError: (id) => {
      return _getNodeEvaluation(id).match(
        (v) => v.error,
        (e) => null
      );
    },
    getGraphTemplate: () => {
      return _getNodeTemplate(TERMINAL_NODE_ID).match(
        (v) => v,
        (e) => undefined
      );
    },
    getGraphEvaluation: () => {
      return _getNodeEvaluation(TERMINAL_NODE_ID);
    },
  };
};
